import {AtUri} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {
  type CapsuleDraftRef,
  capsuleDraftRefFromView,
  type CapsuleDraftView,
} from '#/lib/capsuleDrafts'
import {PUBLIC_BSKY_SERVICE} from '#/lib/constants'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'
import {type Slide} from '#/types/capsule'

const capsuleByPostQueryKeyRoot = 'capsuleByPost'
const capsuleByUriQueryKeyRoot = 'capsuleByUri'
const capsuleDraftByUriQueryKeyRoot = 'capsuleDraftByUri'
const actorCapsulesQueryKeyRoot = 'actorCapsules'
const actorCapsuleDraftsQueryKeyRoot = 'actorCapsuleDrafts'

/**
 * One of an author's own capsule records, as a picker or grid needs to list it.
 */
export type ActorCapsuleRecord = {
  /** AT-URI of the `app.bsky.feed.capsule` record. */
  uri: string
  cid: string
  title: string
  description: string
  themeId?: string
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  /** Exercises in the capsule, as the publisher counted them. */
  slideCount?: number
  status?: string
  /** The post this capsule was published as. Absent for a draft. */
  postUri?: string
  /** `classroom` when the capsule was written for one room. */
  visibility?: string
  createdAt: string
}

export const createActorCapsulesQueryKey = (actor: string) =>
  createQueryKey(actorCapsulesQueryKeyRoot, {actor})

/**
 * Every capsule record an author has written, drafts included.
 *
 * Read from the repo rather than from the author's post feed, for the reason the
 * book picker reads records: a *post* carries a back-reference to its capsule and
 * none of the capsule's own fields, so a picker built from posts can show a title
 * and nothing else and has no way to filter by year or subject at all. One
 * `listRecords` answers every question a chooser has at once.
 */
export function useActorCapsulesQuery(actor: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createActorCapsulesQueryKey(actor ?? ''),
    enabled: !!actor,
    queryFn: async (): Promise<ActorCapsuleRecord[]> => {
      const out: ActorCapsuleRecord[] = []
      let cursor: string | undefined
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: actor!,
          collection: 'app.bsky.feed.capsule',
          limit: 100,
          cursor,
        })
        for (const record of res.data.records) {
          const value = (record.value ?? {}) as Record<string, unknown>
          const slides = Array.isArray(value.slides) ? value.slides : undefined
          out.push({
            uri: record.uri,
            cid: record.cid ?? '',
            title: (value.title as string | undefined) ?? '',
            description: (value.description as string | undefined) ?? '',
            themeId: (value.themeId as string | undefined) ?? undefined,
            level: (value.level as string | undefined) ?? undefined,
            subject: (value.subject as string | undefined) ?? undefined,
            slideCount:
              typeof value.slideCount === 'number'
                ? value.slideCount
                : slides?.length,
            status: (value.status as string | undefined) ?? undefined,
            postUri:
              ((value.post as {uri?: string} | undefined)?.uri as
                | string
                | undefined) ?? undefined,
            visibility: (value.visibility as string | undefined) ?? undefined,
            createdAt: (value.createdAt as string | undefined) ?? '',
          })
        }
        if (!res.data.cursor || res.data.records.length < 100) break
        cursor = res.data.cursor
      }
      return out
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createCapsuleByUriQueryKey = (capsuleUri: string) =>
  createQueryKey(capsuleByUriQueryKeyRoot, {capsuleUri})

/**
 * Fetches an `app.bsky.feed.capsule` record directly by its AT URI.
 * Much cheaper than useCapsuleByPostQuery when the URI is already known
 * (i.e. the post record has a `capsule: { uri }` back-ref).
 */
export function useCapsuleByUriQuery(capsuleUri: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createCapsuleByUriQueryKey(capsuleUri ?? ''),
    enabled: !!capsuleUri,
    queryFn: async () => {
      const atUri = new AtUri(capsuleUri!)
      const res = await agent.com.atproto.repo.getRecord({
        repo: atUri.hostname,
        collection: atUri.collection,
        rkey: atUri.rkey,
      })
      const value = res.data.value as Record<string, unknown>
      return {
        uri: res.data.uri,
        cid: res.data.cid,
        slides: (value.slides as Slide[] | undefined) ?? [],
        slideCount: value.slideCount as number | undefined,
        title: value.title as string | undefined,
        subject: value.subject as string | undefined,
        level: value.level as string | undefined,
        themeId: value.themeId as string | undefined,
        /**
         * Which minute of a long cover film this capsule opens on.
         *
         * Absent for a film that already fits, which is why it is optional and
         * not zero-valued: the player reads "no window" from its absence.
         */
        coverStartMs: value.coverStartMs as number | undefined,
        behaviorSettings:
          (value.behaviorSettings as Record<string, unknown> | undefined) ?? {},
      }
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createCapsuleByPostQueryKey = (postUri: string) =>
  createQueryKey(capsuleByPostQueryKeyRoot, {postUri})

/**
 * Fetches the `app.bsky.feed.capsule` record whose `post.uri` matches the
 * given post URI. Slides live in that record, not in the feed post itself.
 */
export function useCapsuleByPostQuery(postUri: string, authorDid: string) {
  const agent = useAgent()

  return useQuery({
    queryKey: createCapsuleByPostQueryKey(postUri),
    enabled: !!postUri && !!authorDid,
    queryFn: async () => {
      let cursor: string | undefined
      // Page through the author's capsule records until we find one that
      // references this post. Authors rarely have many capsules so this is fast.
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: authorDid,
          collection: 'app.bsky.feed.capsule',
          limit: 50,
          cursor,
        })
        const match = res.data.records.find(
          r =>
            r.value &&
            typeof r.value === 'object' &&
            (r.value as Record<string, unknown>).post &&
            (
              (r.value as Record<string, unknown>).post as Record<
                string,
                unknown
              >
            ).uri === postUri,
        )
        if (match) {
          const value = match.value as Record<string, unknown>
          console.log(
            '[useCapsuleByPostQuery] found capsule:',
            match.uri,
            value,
          )
          return {
            uri: match.uri,
            cid: match.cid,
            slides: (value.slides as Slide[] | undefined) ?? [],
            slideCount: value.slideCount as number | undefined,
            title: value.title as string | undefined,
            subject: value.subject as string | undefined,
            level: value.level as string | undefined,
            themeId: value.themeId as string | undefined,
            /** See `useCapsuleByUriQuery` — the chosen cover minute, if any. */
            coverStartMs: value.coverStartMs as number | undefined,
            behaviorSettings:
              (value.behaviorSettings as Record<string, unknown> | undefined) ??
              {},
          }
        }
        if (!res.data.cursor || res.data.records.length < 50) {
          // No match found
          console.log(
            '[useCapsuleByPostQuery] no capsule found for post:',
            postUri,
          )
          return null
        }
        cursor = res.data.cursor
      }
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

/**
 * One capsule draft, as the studio needs it to carry on editing.
 *
 * Deliberately not `useCapsuleByUriQuery` with a flag. That query reads what a
 * *player* needs — the slides, the theme, the behaviour settings — and drops the
 * description, because nothing that plays a capsule shows one. Reopening a draft
 * needs the whole editable capsule, and a loader that quietly lost the description
 * would look like the draft had been saved without it.
 */
export const createCapsuleDraftByUriQueryKey = (capsuleUri: string) =>
  createQueryKey(capsuleDraftByUriQueryKeyRoot, {capsuleUri})

export function useCapsuleDraftByUriQuery(capsuleUri: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createCapsuleDraftByUriQueryKey(capsuleUri ?? ''),
    enabled: !!capsuleUri,
    queryFn: async () => {
      const atUri = new AtUri(capsuleUri!)
      const res = await agent.com.atproto.repo.getRecord({
        repo: atUri.hostname,
        collection: atUri.collection,
        rkey: atUri.rkey,
      })
      const value = res.data.value as Record<string, unknown>
      return {
        uri: res.data.uri,
        title: (value.title as string | undefined) ?? '',
        description: (value.description as string | undefined) ?? '',
        themeId: (value.themeId as string | undefined) ?? 'default',
        htmlThemeUrl: (
          value.behaviorSettings as {htmlThemeUrl?: string} | undefined
        )?.htmlThemeUrl,
        slides: (value.slides as Slide[] | undefined) ?? [],
        // Carried so that saving over this draft keeps its original creation date
        // instead of backdating it to the moment it was re-saved.
        createdAt: (value.createdAt as string | undefined) ?? '',
      }
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createActorCapsuleDraftsQueryKey = (actor: string) =>
  createQueryKey(actorCapsuleDraftsQueryKeyRoot, {actor})

/**
 * The creator's own unpublished capsules, from the server.
 *
 * Drafts are private records — the AppView shows them only to the actor
 * themselves — so they can never be reached from a feed, and asking for them by
 * author is the only way back. Asking the server rather than reading a device-local
 * list is the whole point: half-finished work has to be there when the creator
 * opens the studio on their laptop.
 *
 * Two details that are easy to get wrong and fail silently:
 *
 *  • **The host is the AppView.** `getActorCapsules` decides which drafts a viewer
 *    may see, which is a reading concern, so it is not served by the PDS. It is
 *    called through `fetch` rather than the agent for the same reason as its
 *    FlashBook counterpart: it is a Taderok extension that is not among the custom
 *    lexicons the frontend registers.
 *  • **A deployment without it is not an error.** 501 and 404 both mean "no drafts
 *    from the server", which the merge treats as "keep what this device has" — the
 *    behaviour the studio had before this list existed. The feature switches itself
 *    on when the backend that implements the handler is deployed.
 */
export function useActorCapsuleDraftsQuery(actor: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createActorCapsuleDraftsQueryKey(actor ?? ''),
    enabled: !!actor,
    queryFn: async (): Promise<CapsuleDraftRef[]> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const params = new URLSearchParams({
        actor: actor!,
        status: 'draft',
        limit: '50',
      })
      const res = await fetch(
        `${PUBLIC_BSKY_SERVICE}/xrpc/app.bsky.feed.getActorCapsules?${params}`,
        {headers: {Authorization: `Bearer ${accessJwt}`}},
      )
      if (!res.ok) {
        if (res.status === 501 || res.status === 404) return []
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {capsules?: CapsuleDraftView[]}
      return (data.capsules ?? [])
        .map(capsuleDraftRefFromView)
        .filter((draft): draft is CapsuleDraftRef => draft !== null)
    },
    // Brief: drafts change whenever the creator saves, and a stale row is worse
    // than a small refetch.
    staleTime: 1000 * 30,
  })
}
